'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

export default function UserActions({ 
  targetUserId, 
  currentStatus, 
  warningCount 
}: { 
  targetUserId: string, 
  currentStatus: string, 
  warningCount: number 
}) {
  const [loading, setLoading] = useState(false)
  const [isWarning, setIsWarning] = useState(false)
  const [reason, setReason] = useState('')
  const router = useRouter()

  // Make confirmMessage optional so we can bypass window.confirm for the custom warning UI
  const handleAction = async (payload: any, confirmMessage?: string) => {
    if (confirmMessage && !window.confirm(confirmMessage)) return

    setLoading(true)
    try {
      const res = await fetch('/api/admin/users', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: targetUserId, ...payload })
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to update user')
      }
      
      // Reset UI state on success
      setIsWarning(false)
      setReason('')
      router.refresh()
    } catch (error: any) {
      console.error(error)
      alert(error.message || 'Action failed')
    } finally {
      setLoading(false)
    }
  }

  const submitWarning = () => {
    if (!reason.trim()) {
      alert('Please provide a reason for the strike.')
      return
    }
    handleAction({ action: 'warn', reason })
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col gap-3 mt-4">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Mod Actions:</span>
        
        {currentStatus !== 'BANNED' && !isWarning && (
          <button
            onClick={() => setIsWarning(true)}
            disabled={loading}
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          >
            Issue Strike ({warningCount}/3)
          </button>
        )}

        {currentStatus === 'BANNED' && (
          <button
            onClick={() => handleAction({ action: 'reset_warnings' }, 'Unban user and reset their strikes to 0?')}
            disabled={loading}
            className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/30 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
          >
            Unban & Reset Strikes
          </button>
        )}
      </div>

      {/* Warning Reason Input UI */}
      {isWarning && (
        <div className="flex flex-col gap-2 mt-2 pt-3 border-t border-slate-800">
          <label className="text-xs text-slate-300 font-semibold">
            Reason for Strike (Required):
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Detail the rule violation..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-sm text-slate-200 focus:border-amber-500 outline-none resize-none h-20"
            disabled={loading}
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                setIsWarning(false)
                setReason('')
              }}
              disabled={loading}
              className="px-3 py-1.5 text-xs text-slate-400 hover:text-slate-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submitWarning}
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-1.5 rounded-lg text-xs font-bold transition-colors disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Confirm Strike'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
